import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

// Profit Contribution Curve: trades sorted best-to-worst, cumulative %
const contributionData = [
  { x: 0, y: 0 }, { x: 2, y: 12 }, { x: 5, y: 28 }, { x: 8, y: 40 },
  { x: 10, y: 52 }, { x: 13, y: 60 }, { x: 16, y: 67 }, { x: 19, y: 72 },
  { x: 20, y: 78 }, { x: 24, y: 82 }, { x: 29, y: 86 }, { x: 35, y: 89 },
  { x: 40, y: 91 }, { x: 48, y: 93 }, { x: 55, y: 95 }, { x: 65, y: 96 },
  { x: 75, y: 97 }, { x: 85, y: 98 }, { x: 95, y: 99 }, { x: 100, y: 100 },
];

// Outcome Distribution: P/L buckets (linear scale)
const histogramData = [
  { bucket: '-1305', count: 1 },
  { bucket: '-840', count: 5 },
  { bucket: '-374', count: 17 },
  { bucket: '91', count: 15 },
  { bucket: '556', count: 10 },
  { bucket: '1022', count: 6 },
  { bucket: '1487', count: 5 },
  { bucket: '1952', count: 3 },
];

export default function TradeDistributionMock() {
  const sectionHeader: React.CSSProperties = {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#e5e7eb',
    marginBottom: '0.125rem',
  };

  const sectionSub: React.CSSProperties = {
    fontSize: '0.5rem',
    color: '#9ca3af',
    marginBottom: '0.5rem',
  };

  const badge = (label: string, value: string, bg: string): React.ReactNode => (
    <span style={{
      display: 'inline-block',
      padding: '0.125rem 0.5rem',
      borderRadius: 4,
      background: bg,
      fontSize: '0.625rem',
      fontWeight: 700,
      color: '#e5e7eb',
      fontFamily: 'monospace',
      marginRight: '0.375rem',
    }}>
      {label}: <span style={{ color: '#fff' }}>{value}</span>
    </span>
  );

  const sizeBar = (label: string, pct: number, count: number) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
      <span style={{ width: 50, fontSize: '0.625rem', color: '#9ca3af', flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 10, background: '#1f2937', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: '#fcd34dB3', borderRadius: 4 }} />
      </div>
      <span style={{ width: 55, textAlign: 'right', fontSize: '0.625rem', fontFamily: 'monospace', color: '#e5e7eb', flexShrink: 0 }}>
        {pct}% ({count})
      </span>
    </div>
  );

  return (
    <div className="platform-mock" style={{ maxWidth: '100%' }}>
      <div className="platform-mock-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span><span className="dot" style={{ background: '#7dd3fc' }} /> Trade Distribution</span>
        <span style={{ fontSize: '0.75rem', color: '#4b5563', cursor: 'default' }}>x</span>
      </div>
      <div className="platform-mock-body" style={{ padding: '0.75rem 1rem' }}>
        {/* Subtitle */}
        <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginBottom: '0.75rem' }}>
          Structure of profit generation across 62 trades
        </div>

        {/* 2x2 grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '0.5rem' }}>
          {/* Panel 1: Profit Contribution Curve */}
          <div style={{ background: '#0f1a28', borderRadius: 6, padding: '0.625rem 0.75rem', border: '1px solid #1f2937' }}>
            <div style={sectionHeader}>Profit Contribution Curve</div>
            <div style={sectionSub}>Cumulative profit from trades sorted best to worst</div>
            <div style={{ position: 'relative' }}>
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={contributionData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="contrib-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7DD3FC" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#7DD3FC" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="x"
                    tick={{ fill: '#4b5563', fontSize: 8 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <YAxis
                    tick={{ fill: '#4b5563', fontSize: 8 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v}%`}
                    domain={[0, 100]}
                  />
                  <ReferenceLine x={20} stroke="#0EA5E9" strokeDasharray="4 4" strokeWidth={1} />
                  <Area
                    type="monotone"
                    dataKey="y"
                    stroke="#7DD3FC"
                    strokeWidth={1.5}
                    fill="url(#contrib-grad)"
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
              {/* "78% of profit" annotation */}
              <div style={{
                position: 'absolute',
                top: 6,
                left: '22%',
                fontSize: '0.5rem',
                color: '#0EA5E9',
                fontWeight: 600,
                pointerEvents: 'none',
              }}>
                78% of profit
              </div>
            </div>
            <div style={{ marginTop: '0.375rem', display: 'flex', gap: '0.375rem' }}>
              {badge('Top 10%', '52%', 'rgba(12,74,110,0.3)')}
              {badge('Top 20%', '78%', 'rgba(12,74,110,0.3)')}
            </div>
          </div>

          {/* Panel 2: Outcome Distribution */}
          <div style={{ background: '#0f1a28', borderRadius: 6, padding: '0.625rem 0.75rem', border: '1px solid #1f2937' }}>
            <div style={sectionHeader}>Outcome Distribution</div>
            <div style={sectionSub}>Trade P/L buckets (linear scale)</div>
            <ResponsiveContainer width="100%" height={120}>
              <BarChart data={histogramData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <XAxis
                  dataKey="bucket"
                  tick={{ fill: '#4b5563', fontSize: 7 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: '#4b5563', fontSize: 8 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Bar
                  dataKey="count"
                  fill="#DDD6FE"
                  fillOpacity={0.7}
                  radius={[2, 2, 0, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ marginTop: '0.375rem', display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
              {badge('Mean', '124', 'rgba(76,29,149,0.3)')}
              {badge('Median', '-65', 'rgba(76,29,149,0.3)')}
            </div>
            <div style={{ fontSize: '0.5rem', color: '#9ca3af', fontStyle: 'italic', marginTop: 4 }}>
              Mean exceeds median, indicating positively skewed payoff distribution
            </div>
          </div>

          {/* Panel 3: Contribution by Size + Streak Characteristics */}
          <div style={{ background: '#0f1a28', borderRadius: 6, padding: '0.625rem 0.75rem', border: '1px solid #1f2937' }}>
            <div style={sectionHeader}>Contribution by Size</div>
            <div style={sectionSub}>Size categories defined by absolute P/L percentiles (33rd/66th)</div>
            {sizeBar('Small', 6, 21)}
            {sizeBar('Medium', 26, 20)}
            {sizeBar('Large', 68, 21)}

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
              <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.375rem' }}>
                Streak Characteristics
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.25rem', fontSize: '0.625rem' }}>
                <div style={{ color: '#9ca3af' }}>Max win streak</div>
                <div style={{ textAlign: 'right', fontWeight: 700, color: '#e5e7eb', fontFamily: 'monospace' }}>3</div>
                <div style={{ color: '#9ca3af' }}>Max loss streak</div>
                <div style={{ textAlign: 'right', fontWeight: 700, color: '#e5e7eb', fontFamily: 'monospace' }}>9</div>
                <div style={{ color: '#9ca3af' }}>Win/Loss</div>
                <div style={{ textAlign: 'right', fontWeight: 700, color: '#e5e7eb', fontFamily: 'monospace' }}>27/35</div>
                <div style={{ color: '#9ca3af' }}>Positive trades</div>
                <div style={{ textAlign: 'right', fontWeight: 700, color: '#e5e7eb', fontFamily: 'monospace' }}>44%</div>
              </div>
            </div>
          </div>

          {/* Panel 4: Observed Structure */}
          <div style={{ background: '#0f1a28', borderRadius: 6, padding: '0.625rem 0.75rem', border: '1px solid #1f2937' }}>
            <div style={sectionHeader}>Observed Structure</div>
            <div style={{ marginTop: '0.375rem' }}>
              <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.25rem' }}>
                Distribution Pattern
              </div>
              <div style={{ fontSize: '0.6875rem', color: '#d1d5db', lineHeight: 1.7 }}>
                Profit outcomes are highly concentrated in a small number of large trades. The majority of trades contribute marginally or negatively.
              </div>
            </div>
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.25rem' }}>
                Implications
              </div>
              <div style={{ fontSize: '0.6875rem', color: '#d1d5db', lineHeight: 1.7 }}>
                Performance outcomes are driven by capturing large moves rather than frequent wins. Variance in outcomes is expected.
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="platform-mock-caption">
        The Trade Distribution modal — profit contribution curve, outcome histogram, size-based breakdown, and structural interpretation.
      </div>
    </div>
  );
}
